import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { IconLogo } from '@components/icons';
import logoUrl from '@images/logo1.png';

const StyledLoader = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: #020709;
  opacity: ${p => (p.$leaving ? 0 : 1)};
  transition: opacity 320ms ease;
  &:focus {
    outline: none;
  }
  .intro-scene {
    position: absolute;
    inset: 0;
    opacity: ${p => (p.$ready ? 1 : 0)};
    transition: opacity 500ms;
  }
  canvas {
    display: block;
    width: 100%;
    height: 100%;
  }
  .fallback {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    background: radial-gradient(ellipse at center, #0a292f, #020709 65%);
  }
  .fallback svg {
    width: min(65vw, 430px);
    height: auto;
    color: var(--green);
  }
  .sr-only,
  .skip {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .skip:focus {
    top: 24px;
    right: 24px;
    width: auto;
    height: auto;
    margin: 0;
    padding: 12px 18px;
    clip: auto;
    z-index: 2;
    color: var(--green);
    background: #040d10;
    border: 1px solid var(--green);
    font-family: var(--font-mono);
    font-size: 12px;
  }
  @media (prefers-reduced-motion: reduce) {
    &,
    * {
      transition: none;
    }
  }
`;

const Loader = ({ finishLoading }) => {
  const rootRef = useRef(null);
  const sceneRef = useRef(null);
  const skipRef = useRef(null);
  const completedRef = useRef(false);
  const exitTimerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [stage, setStage] = useState('morph');
  const [deal, setDeal] = useState('flop');
  const [leaving, setLeaving] = useState(false);

  const skip = useCallback(() => {
    if (completedRef.current) {
      return;
    }
    completedRef.current = true;
    setLeaving(true);
    exitTimerRef.current = window.setTimeout(finishLoading, 320);
  }, [finishLoading]);

  useEffect(() => {
    let cancelled = false;
    let controller;
    let fallbackTimer;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    rootRef.current?.focus({ preventScroll: true });
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const complete = () => {
      if (!cancelled && !completedRef.current) {
        completedRef.current = true;
        finishLoading();
      }
    };
    const fallback = () => {
      if (!cancelled) {
        setFailed(true);
        setReady(true);
        window.clearTimeout(fallbackTimer);
        fallbackTimer = window.setTimeout(complete, 1200);
      }
    };
    if (motion.matches) {
      complete();
    } else {
      import('./intro/scene')
        .then(({ default: createIntroScene }) => {
          if (cancelled) {
            return null;
          }
          return createIntroScene({
            container: sceneRef.current,
            logoUrl,
            onReady: () => {
              if (!cancelled) {
                setReady(true);
              }
            },
            onStage: value => {
              if (!cancelled) {
                setStage(value);
              }
            },
            onDeal: value => {
              if (!cancelled) {
                setDeal(value);
              }
            },
            onComplete: complete,
            onError: fallback,
          });
        })
        .then(value => {
          controller = value;
          if (cancelled) {
            controller?.dispose();
          }
        })
        .catch(fallback);
    }
    const onMotionChange = event => {
      if (event.matches) {
        complete();
      }
    };
    motion.addEventListener('change', onMotionChange);
    return () => {
      cancelled = true;
      window.clearTimeout(exitTimerRef.current);
      window.clearTimeout(fallbackTimer);
      motion.removeEventListener('change', onMotionChange);
      controller?.dispose();
      document.body.style.overflow = previousOverflow;
    };
  }, [finishLoading]);

  useEffect(() => {
    const onKey = event => {
      if (completedRef.current || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      if (event.key === 'Escape' || event.key === 'Enter') {
        event.preventDefault();
        skip();
      } else if (event.key === 'Tab') {
        event.preventDefault();
        skipRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skip]);

  return (
    <StyledLoader
      ref={rootRef}
      className="intro-shell"
      role="dialog"
      aria-modal="true"
      aria-label="Alans Fuentes introduction"
      aria-describedby="intro-description"
      tabIndex={-1}
      data-intro-stage={stage}
      data-intro-deal={deal}
      $ready={ready}
      $leaving={leaving || stage === 'exit'}>
      <div ref={sceneRef} className="intro-scene" aria-hidden="true" />
      {failed && (
        <div className="fallback" aria-hidden="true">
          <IconLogo />
        </div>
      )}
      <button ref={skipRef} type="button" className="skip" onClick={skip}>
        Skip introduction
      </button>
      <p id="intro-description" className="sr-only">
        A short ASCII animation: a loading bar becomes a snake that traces my alpha logo, then an
        ace. The flop, turn, and river reveal Alans. The website opens automatically. Press Escape
        or Enter to skip.
      </p>
    </StyledLoader>
  );
};
Loader.propTypes = { finishLoading: PropTypes.func.isRequired };
export default Loader;
